import { useEffect, useRef, useState } from 'react'

interface MediaDeletion {
  persist: () => Promise<boolean>
  removeLocal: () => void
  removeComments: () => unknown
}

/** Keep the preview and its comments intact until the result write is confirmed. */
export function useConfirmedMediaDeletion(scope: string) {
  const mounted = useRef(true)
  const currentScope = useRef(scope)
  const pending = useRef(false)
  const [busy, setBusy] = useState(false)
  const [failedScope, setFailedScope] = useState<string | null>(null)
  currentScope.current = scope

  const remove = async (operation: MediaDeletion) => {
    if (pending.current) return false
    const requestScope = scope
    const isCurrent = () => mounted.current && currentScope.current === requestScope
    pending.current = true
    setBusy(true)
    setFailedScope(null)

    try {
      if (!(await operation.persist())) {
        if (isCurrent()) setFailedScope(requestScope)
        return false
      }

      if (isCurrent()) operation.removeLocal()
      // Cleanup belongs to the captured result even if the gallery has since closed.
      Promise.resolve().then(operation.removeComments).catch(console.error)
      return true
    } catch (error) {
      if (isCurrent()) setFailedScope(requestScope)
      return false
    } finally {
      pending.current = false
      if (mounted.current) setBusy(false)
    }
  }

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  return { remove, busy, failed: failedScope === scope }
}
